import React, { useState, useEffect } from 'react';
import AdminAnalytics from './AdminAnalytics';
import AdminSupportPanel from './AdminSupportPanel';
import AdminAiTelemetry from './AdminAiTelemetry';
import AdminChurnAndFunnel from './AdminChurnAndFunnel';
import { 
  Crown, 
  UserCog, 
  Eye, 
  Trash2, 
  Edit2, 
  ChevronDown, 
  ChevronRight, 
  Search, 
  ListTree, 
  AlignJustify, 
  Zap,
  LayoutDashboard,
  Cpu,
  UserCheck
} from 'lucide-react';

const getTimeAgo = (dateStr) => {
  if (!dateStr) return 'Nunca';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " años";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " meses";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " d";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " h";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " min";
  return "Ahora";
};

const AdminDashboard = () => {
  const [adminTab, setAdminTab] = useState('overview'); // 'overview' | 'ai' | 'churn'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Paginación y Filtrado
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterPro, setFilterPro] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [treeMode, setTreeMode] = useState(false);
  const [expandedCoaches, setExpandedCoaches] = useState({});

  // Modals state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInvitationsModalOpen, setIsInvitationsModalOpen] = useState(false);
  const [invitationsHistory, setInvitationsHistory] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ id: '', name: '', email: '', role: '' });
  
  // Forms state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('athlete');
  const [inviteLink, setInviteLink] = useState('');
  const [selectedCoachId, setSelectedCoachId] = useState('');
  
  // Bulk action state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [selectedUserForBreakdown, setSelectedUserForBreakdown] = useState(null);

  // Broadcast state
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);

  const fetchGlobalStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');
      const res = await fetch(`${API_URL}/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGlobalStats(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page,
        limit,
        search: searchQuery,
        sortBy,
        sortOrder,
        tree: treeMode
      });
      if (filterRole) params.append('role', filterRole);
      if (filterPro) params.append('isPro', 'true');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al obtener la lista de usuarios');
      const data = await res.json();
      setUsers(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitationsHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/invitations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al recuperar historial');
      const data = await res.json();
      setInvitationsHistory(data);
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    fetchGlobalStats();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [page, limit, sortBy, sortOrder, treeMode, filterRole, filterPro]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchUsers();
    }, 500);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const handleExportCSV = () => {
    const csvHeader = 'ID,Nombre,Email,Rol,Plan,Ultimo Login\n';
    const csvContent = users.map(u => `"${u.id}","${u.name}","${u.email}","${u.role}","${u.subscriptionPlan || 'free'}","${u.lastLoginAt}"`).join('\n');
    const blob = new Blob([csvHeader + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'usuarios.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar a ${name}? Esta acción no se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar usuario');
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAssignCoach = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/${selectedUser.id}/coach`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ coachId: selectedCoachId || null })
      });
      if (!res.ok) throw new Error('Error al reasignar entrenador');
      
      await fetchUsers();
      setIsAssignModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/${editFormData.id}/edit`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editFormData.name, email: editFormData.email, role: editFormData.role })
      });
      if (!res.ok) throw new Error('Error al editar usuario');
      
      await fetchUsers();
      setIsEditModalOpen(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleBulkAssignCoach = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/bulk-coach`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ athleteIds: selectedIds, coachId: selectedCoachId || null })
      });
      if (!res.ok) throw new Error('Error al reasignar entrenadores masivamente');
      
      await fetchUsers(); // Refresh the list
      setIsBulkAssignModalOpen(false);
      setSelectedIds([]);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/invite`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail, name: inviteName, coachId: selectedCoachId || null, role: inviteRole })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Error al generar invitación');
      }
      
      const data = await res.json();
      setInviteLink(data.link);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.filter(u => u.role === 'athlete').map(u => u.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectUser = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
    );
  };

  const handleImpersonate = async (id, name, originalRole) => {
    if (!window.confirm(`¿Estás seguro de que quieres iniciar sesión como ${name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/${id}/impersonate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al impersonar usuario');
      
      const data = await res.json();
      
      // Save current admin token
      localStorage.setItem('adminToken', token);
      
      // Set new identity
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('appMode', originalRole === 'trainer' ? 'coach' : 'athlete');
      
      // Reload page to apply identity
      window.location.href = '/app';
    } catch (err) {
      alert(err.message);
    }
  };

  const handleTogglePro = async (id, name, currentTier) => {
    const isPro = currentTier === 'PRO';
    const action = isPro ? 'revocar' : 'conceder';
    if (!window.confirm(`¿Estás seguro de que quieres ${action} el acceso PRO a ${name}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/users/${id}/subscription`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscriptionTier: isPro ? 'FREE' : 'PRO' })
      });
      if (!res.ok) throw new Error('Error al actualizar la suscripción');
      
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastMsg.trim()) return;
    if (!window.confirm('¿Enviar este anuncio global a los usuarios seleccionados?')) return;
    
    setSendingBroadcast(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api')}/admin/broadcast`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: broadcastMsg, target: broadcastTarget })
      });
      if (!res.ok) throw new Error('Error al enviar el anuncio');
      
      const data = await res.json();
      alert(`✅ Anuncio enviado con éxito a ${data.count} usuarios.`);
      setBroadcastMsg('');
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingBroadcast(false);
    }
  };

  if (loading) {
    return <div className="glass-panel" style={{ padding: '3rem', textAlign: 'center' }}>Cargando panel de administración...</div>;
  }

  if (error) {
    return <div className="glass-panel" style={{ padding: '3rem', textAlign: 'center', color: '#ef4444' }}>{error}</div>;
  }

  const totalUsers = users.length;
  const totalCoaches = users.filter(u => u.role === 'trainer').length;
  const totalAthletes = users.filter(u => u.role === 'athlete').length;
  const totalPro = users.filter(u => u.subscriptionTier === 'PRO').length;
  const coachesList = users.filter(u => u.role === 'trainer');

  const aiTotalMonthly = users.reduce((sum, u) => sum + (u.aiCostMonthly || 0), 0);
  const usersWithAiCost = users.filter(u => (u.aiCostMonthly || 0) > 0);
  const aiAvgMonthly = usersWithAiCost.length ? aiTotalMonthly / usersWithAiCost.length : 0;
  const topSpender = [...users].sort((a, b) => (b.aiCostMonthly || 0) - (a.aiCostMonthly || 0))[0];

  return (
    <div className="dashboard-main" style={{ animation: 'fadeInTab 0.4s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
        <div>
          <h2 className="text-2xl" style={{ color: 'var(--text-primary)', marginBottom: '0.2rem' }}>Panel de Control Global</h2>
          <p className="text-muted text-sm">Visión general y gestión de usuarios</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button 
            onClick={() => { fetchInvitationsHistory(); setIsInvitationsModalOpen(true); }}
            style={{ 
              background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', 
              padding: '0.6rem 1.2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
          >
            Invitaciones
          </button>
          <button 
            onClick={() => { setInviteLink(''); setInviteEmail(''); setInviteName(''); setInviteRole('athlete'); setIsInviteModalOpen(true); }}
            style={{ 
              background: 'var(--health-cyan)', color: 'white', border: 'none', 
              padding: '0.6rem 1.2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={e => e.currentTarget.style.opacity = '1'}
          >
            + Invitar
          </button>
        </div>
      </div>

      {/* MÉTRICAS RÁPIDAS (Top Grid) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Total Usuarios</p>
          <h3 style={{ fontSize: '1.5rem', color: 'white', margin: 0, fontWeight: 800 }}>{totalUsers}</h3>
        </div>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Entrenadores</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--health-cyan)', margin: 0, fontWeight: 800 }}>{totalCoaches}</h3>
        </div>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Atletas</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--health-green)', margin: 0, fontWeight: 800 }}>{totalAthletes}</h3>
        </div>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Cuentas PRO</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--strava-orange)', margin: 0, fontWeight: 800 }}>{totalPro}</h3>
        </div>
        
        {/* Desde globalStats */}
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Usuarios Activos</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--health-cyan)', margin: 0, fontWeight: 800 }}>{globalStats?.activeUsersLast30Days || '-'}</h3>
        </div>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Peticiones IA</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--health-green)', margin: 0, fontWeight: 800 }}>{globalStats?.ai?.totalRequests || '-'}</h3>
        </div>
        <div className="glass-panel" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.2rem', background: 'linear-gradient(145deg, rgba(30,58,138,0.2) 0%, rgba(17,24,39,1) 100%)', border: '1px solid rgba(59,130,246,0.3)' }}>
          <p style={{ margin: 0, color: 'rgba(96,165,250,0.8)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>Coste IA Mes</p>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--health-cyan)', margin: 0, fontWeight: 800 }}>{globalStats ? `€${globalStats.ai.totalCostEur.toFixed(3)}` : '-'}</h3>
        </div>
      </div>

      {/* NAVEGACIÓN DE SECCIONES ADMIN */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.8rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setAdminTab('overview')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: adminTab === 'overview' ? 'rgba(0, 229, 255, 0.15)' : 'transparent',
            color: adminTab === 'overview' ? 'var(--health-cyan)' : 'var(--text-secondary)',
            border: `1px solid ${adminTab === 'overview' ? 'rgba(0, 229, 255, 0.4)' : 'transparent'}`,
            padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.9rem'
          }}
        >
          <LayoutDashboard size={17} />
          Vista General & Soporte
        </button>

        <button
          onClick={() => setAdminTab('ai')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: adminTab === 'ai' ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
            color: adminTab === 'ai' ? '#c4b5fd' : 'var(--text-secondary)',
            border: `1px solid ${adminTab === 'ai' ? 'rgba(139, 92, 246, 0.4)' : 'transparent'}`,
            padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.9rem'
          }}
        >
          <Cpu size={17} />
          Observabilidad IA (Tokens & Costes)
          <span style={{ fontSize: '0.75rem', background: 'rgba(139, 92, 246, 0.25)', color: '#c4b5fd', padding: '0.15rem 0.5rem', borderRadius: '10px', fontWeight: 700 }}>
            €{globalStats?.ai?.totalCostEur ? globalStats.ai.totalCostEur.toFixed(2) : '0.00'}
          </span>
        </button>

        <button
          onClick={() => setAdminTab('churn')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: adminTab === 'churn' ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
            color: adminTab === 'churn' ? '#f59e0b' : 'var(--text-secondary)',
            border: `1px solid ${adminTab === 'churn' ? 'rgba(245, 158, 11, 0.4)' : 'transparent'}`,
            padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.9rem'
          }}
        >
          <UserCheck size={17} />
          Embudo & Radar de Churn
          {globalStats?.inactiveAthletes?.length > 0 && (
            <span style={{ fontSize: '0.75rem', background: '#ef4444', color: 'white', padding: '0.15rem 0.5rem', borderRadius: '10px', fontWeight: 700 }}>
              {globalStats.inactiveAthletes.length}
            </span>
          )}
        </button>
      </div>

      {/* VISTA 1: OBSERVABILIDAD IA DEDICADA */}
      {adminTab === 'ai' && (
        <AdminAiTelemetry aiStats={globalStats?.ai} />
      )}

      {/* VISTA 2: EMBUDO & RADAR DE CHURN DEDICADO */}
      {adminTab === 'churn' && (
        <AdminChurnAndFunnel 
          funnel={globalStats?.funnel} 
          inactiveAthletes={globalStats?.inactiveAthletes} 
          onRefresh={fetchGlobalStats} 
        />
      )}

      {/* VISTA 3: VISTA GENERAL */}
      {adminTab === 'overview' && (
        <>
          {/* DASHBOARD LAYOUT */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '1.5rem', alignItems: 'stretch', marginBottom: '2rem' }}>
            <AdminAnalytics stats={globalStats} />
            
            <div className="glass-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.08) 0%, rgba(17, 24, 39, 0.6) 100%)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div style={{ padding: '0.4rem', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa' }}>
                      <Zap size={18} />
                    </div>
                    <h4 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: 700 }}>Eficiencia & Consumo IA</h4>
                  </div>
                  <button 
                    onClick={() => setAdminTab('ai')}
                    style={{ background: 'transparent', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                  >
                    Ver Telemetría ➔
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '0.8rem' }}>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', display: 'block' }}>Mayor Consumidor</span>
                    <span style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600 }}>{topSpender && topSpender.aiCostMonthly > 0 ? topSpender.name : 'Sin consumo'}</span>
                  </div>
                  <span style={{ color: 'var(--strava-orange)', fontWeight: 800, fontSize: '1.1rem' }}>{topSpender && topSpender.aiCostMonthly > 0 ? `€${topSpender.aiCostMonthly.toFixed(3)}` : '€0.000'}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', display: 'block' }}>Coste Medio / Usuario</span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{usersWithAiCost.length} usuarios con IA</span>
                  </div>
                  <span style={{ color: 'var(--health-cyan)', fontWeight: 800, fontSize: '1.1rem' }}>€{aiAvgMonthly.toFixed(3)}</span>
                </div>
              </div>

              <div style={{ marginTop: '1.2rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Peticiones Totales IA</span>
                <span style={{ color: 'var(--health-green)', fontWeight: 700, fontSize: '0.95rem' }}>{globalStats?.ai?.totalRequests || 0} reqs</span>
              </div>
            </div>
          </div>

          <AdminSupportPanel />
        </>
      )}

      <div className="glass-panel" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 className="text-lg" style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>Envío de Anuncios (Broadcast)</h3>
        <form onSubmit={handleSendBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <textarea 
            value={broadcastMsg}
            onChange={e => setBroadcastMsg(e.target.value)}
            placeholder="Escribe el mensaje que llegará a la bandeja de entrada de los usuarios..."
            style={{ width: '100%', padding: '1rem', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', minHeight: '80px', resize: 'vertical' }}
            required
          />
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select 
              value={broadcastTarget}
              onChange={e => setBroadcastTarget(e.target.value)}
              style={{ padding: '0.8rem', borderRadius: '8px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', flex: 1 }}
            >
              <option value="all" style={{ color: 'black' }}>A todos los usuarios</option>
              <option value="athletes" style={{ color: 'black' }}>Solo a Atletas</option>
              <option value="trainers" style={{ color: 'black' }}>Solo a Entrenadores</option>
              <option value="pro" style={{ color: 'black' }}>Solo a usuarios PRO</option>
            </select>
            <button 
              type="submit"
              disabled={sendingBroadcast || !broadcastMsg.trim()}
              style={{ background: 'var(--health-cyan)', color: '#1a1a1a', border: 'none', padding: '0.8rem 2rem', borderRadius: '8px', fontWeight: 'bold', cursor: (sendingBroadcast || !broadcastMsg.trim()) ? 'not-allowed' : 'pointer', opacity: (sendingBroadcast || !broadcastMsg.trim()) ? 0.5 : 1 }}
            >
              {sendingBroadcast ? 'Enviando...' : 'Enviar Anuncio'}
            </button>
          </div>
        </form>
      </div>

      <div className="glass-panel" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 className="text-lg" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserCog size={20} /> Directorio de Usuarios
          </h3>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* Buscador y Filtros */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input 
                  type="text" 
                  placeholder="Buscar usuario..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '0.5rem 1rem 0.5rem 2.2rem', borderRadius: '6px', fontSize: '0.9rem', width: '200px' }}
                />
              </div>
              <select 
                value={filterRole} 
                onChange={e => { setFilterRole(e.target.value); setPage(1); }}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '0.5rem', borderRadius: '6px', fontSize: '0.9rem' }}
              >
                <option value="" style={{ color: 'black' }}>Todos los Roles</option>
                <option value="athlete" style={{ color: 'black' }}>Atletas</option>
                <option value="trainer" style={{ color: 'black' }}>Entrenadores</option>
              </select>
              <button
                onClick={() => { setFilterPro(!filterPro); setPage(1); }}
                style={{ background: filterPro ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255,255,255,0.05)', color: filterPro ? 'var(--strava-orange)' : 'white', border: `1px solid ${filterPro ? 'rgba(245, 158, 11, 0.5)' : 'rgba(255,255,255,0.1)'}`, padding: '0.5rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Solo PRO
              </button>
            </div>
            
            {/* Tree Mode Toggle */}
            <button
              onClick={() => { setTreeMode(!treeMode); setPage(1); }}
              style={{ background: treeMode ? 'rgba(139, 92, 246, 0.2)' : 'transparent', color: treeMode ? '#c4b5fd' : 'var(--text-muted)', border: `1px solid ${treeMode ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255,255,255,0.1)'}`, padding: '0.5rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
              title={treeMode ? "Ver como lista normal" : "Agrupar por entrenador"}
            >
              {treeMode ? <AlignJustify size={16} /> : <ListTree size={16} />}
              {treeMode ? 'Lista' : 'Agrupado'}
            </button>
            
            {selectedIds.length > 0 && !treeMode && (
              <button 
                onClick={() => { setSelectedCoachId(''); setIsBulkAssignModalOpen(true); }}
                style={{ background: 'var(--strava-orange)', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Asignar a {selectedIds.length}
              </button>
            )}
            
            <button 
              onClick={handleExportCSV}
              style={{ background: 'rgba(16, 185, 129, 0.2)', color: 'var(--health-green)', border: '1px solid rgba(16, 185, 129, 0.5)', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              title="Descargar CSV de los usuarios actuales"
            >
              Exportar CSV
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', color: 'var(--text-muted)' }}>
                {!treeMode && (
                  <th style={{ padding: '1rem', width: '40px', textAlign: 'center' }}>
                    <input type="checkbox" onChange={handleSelectAll} checked={users.filter(u => u.role === 'athlete').length > 0 && selectedIds.length > 0 && selectedIds.length === users.filter(u => u.role === 'athlete').length} style={{ cursor: 'pointer' }} />
                  </th>
                )}
                <th style={{ padding: '1rem 1.5rem', fontWeight: 500, cursor: 'pointer' }} onClick={() => { setSortBy('name'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Usuario {sortBy === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Rol</th>
                {!treeMode && <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Entrenador</th>}
                <th style={{ padding: '1rem 1.5rem', fontWeight: 500, cursor: 'pointer' }} onClick={() => { setSortBy('lastLoginAt'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Últ. Acceso {sortBy === 'lastLoginAt' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                {!treeMode && <th style={{ padding: '1rem 1.5rem', fontWeight: 500, textAlign: 'right', cursor: 'pointer' }} onClick={() => { setSortBy('aiCostMonthly'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>Gasto IA (Mes) {sortBy === 'aiCostMonthly' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>}
                <th style={{ padding: '1rem 1.5rem', fontWeight: 500, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <React.Fragment key={u.id}>
                {/* User Row (Trainer or normal user) */}
                <tr style={{ borderTop: '1px solid rgba(255,255,255,0.05)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                  {!treeMode && (
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {u.role === 'athlete' && (
                      <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => handleSelectUser(u.id)} style={{ cursor: 'pointer' }} />
                    )}
                  </td>
                  )}
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {treeMode && (
                        <button onClick={() => setExpandedCoaches({...expandedCoaches, [u.id]: !expandedCoaches[u.id]})} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                          {expandedCoaches[u.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      )}
                      <div>
                        <div style={{ color: 'white', fontWeight: 500 }}>{u.name}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '2px' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none' }}>
                    <span style={{ 
                      padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                      background: u.role === 'admin' ? 'rgba(239, 68, 68, 0.2)' : u.role === 'trainer' ? 'rgba(6, 182, 212, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      color: u.role === 'admin' ? '#ef4444' : u.role === 'trainer' ? '#06b6d4' : '#10b981'
                    }}>
                      {u.role.toUpperCase()}
                    </span>
                    {u.subscriptionTier === 'PRO' && (
                      <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 800, background: 'rgba(245, 158, 11, 0.1)', color: 'var(--strava-orange)', border: '1px solid var(--strava-orange)' }}>
                        PRO
                      </span>
                    )}
                  </td>
                  {!treeMode && (
                  <td style={{ padding: '1rem 1.5rem', color: 'var(--text-secondary)' }}>
                    {u.coach ? u.coach.name : (u.role === 'athlete' ? 'Independiente' : '-')}
                  </td>
                  )}
                  <td style={{ padding: '1rem 1.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {getTimeAgo(u.lastLoginAt)}
                  </td>
                  {!treeMode && (
                  <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                    {u.aiCostMonthly > 0 ? (
                      <span style={{ color: 'var(--text-secondary)', cursor: 'pointer', borderBottom: '1px dashed var(--text-secondary)' }} onClick={() => setSelectedUserForBreakdown(u)}>
                        €{u.aiCostMonthly.toFixed(3)}
                      </span>
                    ) : '-'}
                  </td>
                  )}
                  <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button onClick={() => { setEditFormData({ id: u.id, name: u.name, email: u.email, role: u.role }); setIsEditModalOpen(true); }} title="Editar" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Edit2 size={16} /></button>
                      <button onClick={() => handleTogglePro(u.id, u.name, u.subscriptionTier)} title={u.subscriptionTier === 'PRO' ? 'Quitar PRO' : 'Hacer PRO'} style={{ background: 'transparent', color: u.subscriptionTier === 'PRO' ? 'var(--strava-orange)' : 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Crown size={16} /></button>
                      {u.role === 'athlete' && !treeMode && (
                        <button onClick={() => { setSelectedUser(u); setSelectedCoachId(u.coach ? u.coach.id : ''); setIsAssignModalOpen(true); }} title="Asignar Entrenador" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><UserCog size={16} /></button>
                      )}
                      <button onClick={() => handleImpersonate(u.id, u.name, u.role)} title="Ver como..." style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Eye size={16} /></button>
                      <button onClick={() => handleDeleteUser(u.id, u.name)} title="Eliminar" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
                {/* Child rows for Tree Mode */}
                {treeMode && expandedCoaches[u.id] && u.athletes && u.athletes.map((athlete) => (
                  <tr key={`child-${athlete.id}`} style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '1rem 1.5rem', paddingLeft: '3.5rem' }}>
                      <div style={{ color: 'white', fontSize: '0.9rem' }}>↳ {athlete.name}</div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', paddingLeft: '1rem' }}>{athlete.email}</div>
                    </td>
                    <td style={{ padding: '1rem 1.5rem' }}>
                      <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>{athlete.role.toUpperCase()}</span>
                      {athlete.subscriptionTier === 'PRO' && (
                        <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', borderRadius: '12px', fontSize: '0.65rem', fontWeight: 800, background: 'rgba(245, 158, 11, 0.1)', color: 'var(--strava-orange)', border: '1px solid var(--strava-orange)' }}>PRO</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem 1.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{getTimeAgo(athlete.lastLoginAt)}</td>
                    <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button onClick={() => { setEditFormData({ id: athlete.id, name: athlete.name, email: athlete.email, role: athlete.role }); setIsEditModalOpen(true); }} title="Editar" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Edit2 size={16} /></button>
                        <button onClick={() => handleTogglePro(athlete.id, athlete.name, athlete.subscriptionTier)} title={athlete.subscriptionTier === 'PRO' ? 'Quitar PRO' : 'Hacer PRO'} style={{ background: 'transparent', color: athlete.subscriptionTier === 'PRO' ? 'var(--strava-orange)' : 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Crown size={16} /></button>
                        <button onClick={() => handleImpersonate(athlete.id, athlete.name, athlete.role)} title="Ver como..." style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Eye size={16} /></button>
                        <button onClick={() => handleDeleteUser(athlete.id, athlete.name)} title="Eliminar" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                </React.Fragment>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={treeMode ? 4 : 7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No se encontraron usuarios.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Página {page} de {totalPages}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{ padding: '0.4rem 0.8rem', borderRadius: '4px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: page === 1 ? 'var(--text-muted)' : 'white', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
            >
              Anterior
            </button>
            <button 
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              style={{ padding: '0.4rem 0.8rem', borderRadius: '4px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: page >= totalPages ? 'var(--text-muted)' : 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {/* Modal: Asignar Entrenador */}
      {isAssignModalOpen && selectedUser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '400px', padding: '2rem' }}>
            <h3 style={{ marginTop: 0 }}>Asignar Entrenador</h3>
            <p className="text-muted text-sm">Selecciona un entrenador para <strong>{selectedUser.name}</strong> o desvincúlalo para que sea atleta independiente.</p>
            
            <form onSubmit={handleAssignCoach}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Entrenador Asignado:</label>
                <select 
                  value={selectedCoachId} 
                  onChange={(e) => setSelectedCoachId(e.target.value)}
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}
                >
                  <option value="">(Independiente - Sin Entrenador)</option>
                  {coachesList.map(coach => (
                    <option key={coach.id} value={coach.id}>{coach.name} ({coach.email})</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setIsAssignModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" style={{ background: 'var(--health-green)', color: '#1a1a1a', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Asignar Entrenador Masivamente */}
      {isBulkAssignModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '400px', padding: '2rem' }}>
            <h3 style={{ marginTop: 0 }}>Asignación Masiva</h3>
            <p className="text-muted text-sm">Selecciona el entrenador al que deseas asignar los <strong>{selectedIds.length}</strong> atletas seleccionados.</p>
            
            <form onSubmit={handleBulkAssignCoach}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Entrenador de destino:</label>
                <select 
                  value={selectedCoachId} 
                  onChange={(e) => setSelectedCoachId(e.target.value)}
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}
                >
                  <option value="">(Independiente - Desvincularlos)</option>
                  {coachesList.map(coach => (
                    <option key={coach.id} value={coach.id}>{coach.name} ({coach.email})</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setIsBulkAssignModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" style={{ background: 'var(--strava-orange)', color: 'white', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  Aplicar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Generar Invitación */}
      {isInviteModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '400px', padding: '2rem' }}>
            <h3 style={{ marginTop: 0 }}>Generar Invitación (Magic Link)</h3>
            <p className="text-muted text-sm">Crea un enlace para que un usuario se registre en la plataforma.</p>
            
            {!inviteLink ? (
              <form onSubmit={handleCreateInvite}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Nombre:</label>
                  <input type="text" required value={inviteName} onChange={e => setInviteName(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }} placeholder="Ej. Carlos Martínez" />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Email:</label>
                  <input type="email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }} placeholder="carlos@example.com" />
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Rol:</label>
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}>
                    <option value="athlete">Atleta</option>
                    <option value="trainer">Entrenador</option>
                  </select>
                </div>
                {inviteRole === 'athlete' && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>Vincular a Entrenador (Opcional):</label>
                    <select value={selectedCoachId} onChange={e => setSelectedCoachId(e.target.value)} style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px' }}>
                      <option value="">(Ninguno - Registro Libre)</option>
                      {coachesList.map(coach => (
                        <option key={coach.id} value={coach.id}>{coach.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setIsInviteModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" style={{ background: 'var(--health-cyan)', color: 'white', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    Generar Enlace
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid var(--health-green)', borderRadius: '8px', marginBottom: '1.5rem', wordBreak: 'break-all' }}>
                  <p style={{ color: 'var(--health-green)', margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>¡Enlace Generado!</p>
                  <a href={inviteLink} target="_blank" rel="noreferrer" style={{ color: 'white', fontSize: '0.9rem' }}>{inviteLink}</a>
                </div>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    alert('¡Copiado al portapapeles!');
                  }}
                  style={{ width: '100%', background: 'white', color: 'black', border: 'none', padding: '0.8rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}
                >
                  Copiar Enlace
                </button>
                <button 
                  onClick={() => setIsInviteModalOpen(false)}
                  style={{ width: '100%', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)', padding: '0.8rem', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal: Desglose de Gastos IA */}
      {selectedUserForBreakdown && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '400px', padding: '2rem' }}>
            <h3 style={{ marginTop: 0, color: 'var(--health-cyan)' }}>Desglose de Uso (IA)</h3>
            <p className="text-muted text-sm">Consumo de <strong>{selectedUserForBreakdown.name}</strong> en el mes actual.</p>
            
            <div style={{ margin: '1.5rem 0', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', overflow: 'hidden' }}>
              {selectedUserForBreakdown.aiCostBreakdown && Object.keys(selectedUserForBreakdown.aiCostBreakdown).length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>
                      <th style={{ padding: '0.8rem', textAlign: 'left' }}>Funcionalidad</th>
                      <th style={{ padding: '0.8rem', textAlign: 'right' }}>Peticiones</th>
                      <th style={{ padding: '0.8rem', textAlign: 'right' }}>Coste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedUserForBreakdown.aiCostBreakdown).map(([type, stats], idx) => (
                      <tr key={type} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                        <td style={{ padding: '0.8rem', color: 'white' }}>{type}</td>
                        <td style={{ padding: '0.8rem', textAlign: 'right', color: 'var(--text-secondary)' }}>{stats.count}</td>
                        <td style={{ padding: '0.8rem', textAlign: 'right', color: 'var(--text-secondary)' }}>€{stats.cost.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <td style={{ padding: '0.8rem', fontWeight: 'bold', color: 'white' }}>Total</td>
                      <td style={{ padding: '0.8rem', textAlign: 'right', fontWeight: 'bold', color: 'white' }}>
                        {Object.values(selectedUserForBreakdown.aiCostBreakdown).reduce((s, st) => s + st.count, 0)}
                      </td>
                      <td style={{ padding: '0.8rem', textAlign: 'right', fontWeight: 'bold', color: 'var(--health-cyan)' }}>
                        €{selectedUserForBreakdown.aiCostMonthly.toFixed(3)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No hay datos de consumo registrados.</div>
              )}
            </div>

            <button 
              onClick={() => setSelectedUserForBreakdown(null)}
              style={{ width: '100%', background: 'white', color: 'black', border: 'none', padding: '0.8rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
      {isEditModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '400px', padding: '2rem' }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'white' }}>Editar Usuario</h3>
            <form onSubmit={handleEditUser}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Nombre</label>
                <input 
                  type="text" 
                  value={editFormData.name}
                  onChange={e => setEditFormData({...editFormData, name: e.target.value})}
                  required
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '6px' }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email</label>
                <input 
                  type="email" 
                  value={editFormData.email}
                  onChange={e => setEditFormData({...editFormData, email: e.target.value})}
                  required
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '6px' }}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Rol</label>
                <select 
                  value={editFormData.role}
                  onChange={e => setEditFormData({...editFormData, role: e.target.value})}
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '6px' }}
                >
                  <option value="athlete">Atleta</option>
                  <option value="trainer">Entrenador</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setIsEditModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid rgba(255,255,255,0.1)', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button type="submit" style={{ background: 'var(--health-cyan)', color: '#1a1a1a', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Historial de Invitaciones */}
      {isInvitationsModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="glass-panel" style={{ width: '90%', maxWidth: '800px', padding: '2rem', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'white' }}>Registro de Invitaciones</h3>
              <button onClick={() => setIsInvitationsModalOpen(false)} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: '1.5rem' }}>&times;</button>
            </div>
            
            <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Usuario</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Rol</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Fecha Envío</th>
                    <th style={{ padding: '1rem', textAlign: 'center' }}>Estado</th>
                    <th style={{ padding: '1rem', textAlign: 'right' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {invitationsHistory.map((inv) => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ color: 'white', fontWeight: 600 }}>{inv.name}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{inv.email}</div>
                      </td>
                      <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>{inv.role === 'trainer' ? 'Entrenador' : 'Atleta'}</td>
                      <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        {inv.status === 'accepted' && <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>Aceptada</span>}
                        {inv.status === 'pending' && <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>Pendiente</span>}
                        {inv.status === 'expired' && <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>Caducada</span>}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        {inv.status === 'pending' && (
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(inv.link);
                              alert('Enlace copiado');
                            }}
                            style={{ background: 'var(--glass-border)', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                          >
                            Copiar Enlace
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {invitationsHistory.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No hay invitaciones registradas.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;

